import { Browser } from "@capacitor/browser";
import { Capacitor, CapacitorHttp } from "@capacitor/core";
import { Directory, Filesystem, ProgressStatus } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import { useTranslate } from "@tolgee/react";
import moment from "moment";
import { useSnackbar } from "notistack";
import React from "react";
import { getFileExtension, trimFileExtension } from "../../utils.js";
import { getNativeDownloadFileName } from "./nativeDownloadFileName.js";
import { shareNativeFile } from "./shareNativeFile.js";

/**
 * A URL, or a function resolving one when the user opens the file. Signed
 * URLs expire, so files with a stored owner pass a resolver
 * (`useOpenAttachment`) instead of a URL from an earlier query result.
 */
export type FileUrl = string | (() => Promise<string>);

export type OpenFileOptions = {
  /** Native only: save under the response's Content-Disposition filename. */
  filenameFromResponse?: boolean;
};

export const useOpenFile = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { t } = useTranslate(["Global"]);

  const showOpenFailed = () =>
    enqueueSnackbar(
      t("The file could not be opened. Please try again.", { ns: "Global" }),
      { variant: "error" }
    );

  const openFile = async (
    fileUrl: FileUrl,
    fileFullPath: string,
    opts?: OpenFileOptions
  ) => {
    if (Capacitor.isNativePlatform()) {
      let url: string;
      try {
        url = typeof fileUrl === "string" ? fileUrl : await fileUrl();
      } catch (e) {
        // no URL to fall back to
        showOpenFailed();
        return;
      }

      let removeListener: (() => Promise<void>) | null = null;
      try {
        enqueueSnackbar(
          <div>
            {t("Downloading...", { ns: "Global" })}
            <span id="msys-file-download-percent"></span>
          </div>,
          {
            variant: "default",
            key: "msys-file-download",
            persist: true,
          }
        );

        if (opts?.filenameFromResponse) {
          // Filesystem.downloadFile needs the name up front and drops the
          // response headers, so the stored Content-Disposition is unreachable.
          const response = await CapacitorHttp.get({
            url,
            responseType: "blob",
          });
          if (response.status >= 400 || typeof response.data !== "string")
            throw new Error("Download failed, please try again later");

          await shareNativeFile(
            response.data,
            getNativeDownloadFileName(response.headers, fileFullPath)
          );
          return;
        }

        const { remove } = await Filesystem.addListener(
          "progress",
          (progress: ProgressStatus) => {
            const el = document.querySelector("#msys-file-download-percent");
            const percent =
              progress.contentLength > 0
                ? Math.floor(progress.bytes / progress.contentLength)
                : 0;
            if (el) el.innerHTML = ` ${percent}%`;
          }
        );
        removeListener = remove;

        const result = await Filesystem.downloadFile({
          url,
          path: fileFullPath,
          recursive: true,
          progress: true,
          directory: Directory.Cache,
        });

        if (!result.path)
          throw new Error("Download failed, please try again later");

        const fileLocalPath = result.path.startsWith("file://")
          ? result.path
          : `file://${result.path}`;

        try {
          await Share.share({
            url: fileLocalPath,
          });
        } catch (e) {
          // do nothing with close share dialog
        }
      } catch (e) {
        // if (e instanceof Error)
        //   enqueueSnackbar(e.message, { variant: "error" });
        // fallback
        try {
          await Browser.open({ url, presentationStyle: "fullscreen" });
        } catch (e) {
          const tab = window.open(url, "_system");
          if (tab) tab.focus();
        }
      } finally {
        closeSnackbar("msys-file-download");
        await removeListener?.();
      }
    } else if (typeof fileUrl === "string") {
      const tab = window.open(fileUrl, "_blank");
      if (tab) tab.focus();
    } else {
      // Open the tab inside the click, before awaiting the URL, so popup
      // blockers still treat it as user-initiated.
      const tab = window.open("", "_blank");
      if (tab) tab.opener = null;
      let url: string;
      try {
        url = await fileUrl();
      } catch (e) {
        tab?.close();
        showOpenFailed();
        return;
      }
      if (tab) {
        tab.location.href = url;
        tab.focus();
      } else {
        // The blocker refused the pre-opened tab; a retry after the await is
        // no longer user-initiated, so report a refusal instead of failing
        // silently.
        const fallback = window.open(url, "_blank");
        if (fallback) fallback.focus();
        else showOpenFailed();
      }
    }
  };

  const openPdf = async (
    url: FileUrl,
    fileName: string,
    opts?: OpenFileOptions
  ) => {
    return openFile(
      url,
      `${trimFileExtension(fileName)}_${moment().format(
        "YYYY-MM-DD_HH-mm-ss"
      )}.pdf`,
      opts
    );
  };
  const openAnyFile = async (
    url: FileUrl,
    fileName: string,
    opts?: OpenFileOptions
  ) => {
    return openFile(
      url,
      `${trimFileExtension(fileName)}_${moment().format(
        "YYYY-MM-DD_HH-mm-ss"
      )}.${getFileExtension(fileName)}`,
      opts
    );
  };

  const urlsRef = React.useRef<string[]>([]);

  // revoke created object urls
  React.useEffect(() => {
    return () => {
      const urls = urlsRef.current;
      if (urls.length > 0) {
        urls.forEach(url => {
          try {
            URL.revokeObjectURL(url);
          } catch (e) {
            // nothing to do
          }
        });
      }
    };
  }, []);

  // manually revoke urls
  const revokeUrls = () => {
    const urls = urlsRef.current;
    if (urls.length > 0) {
      urls.forEach(url => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {
          // nothing to do
        }
      });
    }
  };

  return {
    openPdf,
    openAnyFile,
    revokeUrls,
  };
};
